import { useState } from "react";

const FORM_ID = "db8hu21rh5tg6socjum0";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db8hu21rh5tg6socjum0";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    // anti-spam honeypot: silently succeed for bots
    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    payload.delete("website_alt");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMessage);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMessage || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-secondary-300/60 bg-secondary-50 px-6 py-14 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-2xl text-background-50">
          <i className="ri-check-line"></i>
        </span>
        <p className="mt-5 font-heading text-lg font-bold text-foreground-950">提交成功</p>
        <p className="mt-2 text-sm text-foreground-600">感谢你的留言，可可影视会尽快回复你。</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
        >
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-700">姓名</span>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入你的称呼"
            className="rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-700">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="name@example.com"
            className="rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground-700">留言</span>
          <textarea
            name="message"
            required
            maxLength={500}
            rows={5}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="想聊聊哪部影视作品，或对可可影视有什么建议？"
            className="resize-none rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          ></textarea>
          <span className="text-right text-xs text-foreground-400">{message.length} / 500</span>
        </label>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="field-anti-spam"
        />

        {status === "error" && formError && (
          <p className="rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">{formError}</p>
        )}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full whitespace-nowrap rounded-md bg-accent-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          {status === "submitting" ? "提交中..." : "提交留言"}
        </button>
        <p className="text-center text-xs text-foreground-400">
          提交即表示你同意我们通过邮箱回复你的留言。
        </p>
      </div>
    </form>
  );
}